import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, errOf } from '../../lib/api';

interface TemplateRow {
  id: string;
  purpose: string;
  locale: string;
  name: string;
  subject: string;
  body: string;
}
interface GroupRow {
  id: string;
  name: string;
  recipient_count: number;
}
interface SendRow {
  id: string;
  purpose: string;
  subject: string;
  status: string;
  created_at: string;
}
interface EventRow {
  id: string;
  name: string;
  starts_at: string | null;
  venue: string;
  status: string;
}

const field = {
  width: '100%',
  borderRadius: 8,
  padding: '6px 10px',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-small)',
};
const btn = { background: 'var(--color-primary)', color: 'var(--color-on-primary)', border: 'none', cursor: 'pointer' } as const;
const card = { background: 'var(--color-card)', boxShadow: 'var(--shadow-card)' } as const;

/** 管理台「活動信件」—— 模板 ／ 名單 ／ 寄送批次（Phase 1；decisions/008） */
export default function AdminLettersPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [sends, setSends] = useState<SendRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const [tpl, setTpl] = useState({ name: '', purpose: 'invite', locale: 'zh-HK', subject: '', body: '', brief: '' });
  const [grp, setGrp] = useState({ name: '', text: '' });
  const [evt, setEvt] = useState({ name: '', starts_at: '', venue: '' });
  const [pick, setPick] = useState({ templateId: '', groupId: '', eventId: '' });

  const load = useCallback(async () => {
    const [a, b, c, d] = await Promise.all([
      apiGet<{ templates: TemplateRow[] }>('/api/admin/letters/templates'),
      apiGet<{ groups: GroupRow[] }>('/api/admin/letters/groups'),
      apiGet<{ sends: SendRow[] }>('/api/admin/letters/sends'),
      apiGet<{ events: EventRow[] }>('/api/admin/letters/events'),
    ]);
    if (a.ok && a.data) setTemplates(a.data.templates);
    if (b.ok && b.data) setGroups(b.data.groups);
    if (c.ok && c.data) setSends(c.data.sends);
    if (d.ok && d.data) setEvents(d.data.events);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveEvent() {
    setErr('');
    if (evt.name.trim().length < 2) return;
    setBusy(true);
    const r = await apiPost('/api/admin/letters/events', {
      name: evt.name.trim(),
      starts_at: evt.starts_at || null,
      venue: evt.venue.trim(),
    });
    setBusy(false);
    if (!r.ok) {
      setErr(errOf(r.data) || t('ltr_failed'));
      return;
    }
    setEvt({ name: '', starts_at: '', venue: '' });
    setMsg(t('ltr_saved'));
    await load();
  }

  async function aiDraft() {
    setBusy(true);
    setErr('');
    setMsg(t('ltr_ai_working'));
    const r = await apiPost<{ subject: string; body: string; thirdParty: boolean }>('/api/admin/letters/ai-draft', {
      purpose: tpl.purpose,
      locale: tpl.locale,
      brief: tpl.brief,
    });
    setBusy(false);
    setMsg('');
    if (!r.ok || !r.data) {
      setErr(errOf(r.data) || t('ltr_failed'));
      return;
    }
    setTpl({ ...tpl, subject: r.data.subject, body: r.data.body });
    setMsg(r.data.thirdParty ? t('ltr_ai_third_party') : t('ltr_ai_local'));
  }

  async function saveTemplate() {
    setErr('');
    setBusy(true);
    const r = await apiPost('/api/admin/letters/templates', tpl);
    setBusy(false);
    if (!r.ok) {
      setErr(errOf(r.data) || t('ltr_failed'));
      return;
    }
    setTpl({ name: '', purpose: 'invite', locale: 'zh-HK', subject: '', body: '', brief: '' });
    setMsg(t('ltr_saved'));
    await load();
  }

  async function saveGroup() {
    setErr('');
    if (grp.name.trim().length < 2) return;
    setBusy(true);
    const r = await apiPost<{ parsed: number; skipped: number }>('/api/admin/letters/groups', grp);
    setBusy(false);
    if (!r.ok) {
      setErr(errOf(r.data) || t('ltr_failed'));
      return;
    }
    setMsg(t('ltr_grp_parsed', { n: r.data?.parsed ?? 0, skipped: r.data?.skipped ?? 0 }));
    setGrp({ name: '', text: '' });
    await load();
  }

  async function createSend() {
    setErr('');
    if (!pick.templateId || !pick.groupId) {
      setErr(t('ltr_need_pick'));
      return;
    }
    setBusy(true);
    const r = await apiPost<{ id: string; total: number; missing: string[] }>('/api/admin/letters/sends', {
      template_id: pick.templateId,
      group_id: pick.groupId,
      event_id: pick.eventId || null,
    });
    setBusy(false);
    if (!r.ok || !r.data) {
      setErr(errOf(r.data) || t('ltr_failed'));
      return;
    }
    setMsg(
      r.data.missing.length
        ? t('ltr_send_missing', { fields: r.data.missing.join('、') })
        : t('ltr_send_created', { n: r.data.total }),
    );
    await load();
  }

  return (
    <div className="max-w-3xl">
      <h2 className="font-bold text-lg mb-1">{t('ltr_title')}</h2>
      <p className="text-xs mb-4" style={{ color: 'var(--color-text-secondary)' }}>
        {t('ltr_intro')}
      </p>
      {msg && <p className="text-sm mb-2" style={{ color: 'var(--color-primary)' }}>{msg}</p>}
      {err && <p className="text-sm mb-2" style={{ color: 'var(--color-danger)' }}>{err}</p>}

      {/* ── 活動 ─────────────────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-5" style={card}>
        <h3 className="font-bold mb-2">{t('ltr_tab_events')}</h3>
        <ul className="text-sm mb-2">
          {events.length === 0 ? (
            <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
          ) : (
            events.map((e) => (
              <li key={e.id} className="py-1">
                {e.name}
                {e.starts_at ? ` ｜ ${e.starts_at.slice(0, 10)}` : ''}
                {e.venue ? ` ｜ ${e.venue}` : ''}
                {e.status === 'closed' ? ` ｜ ${t('ltr_evt_closed')}` : ''}
              </li>
            ))
          )}
        </ul>
        <input style={field} placeholder={t('ltr_evt_name')} value={evt.name} onChange={(e) => setEvt({ ...evt, name: e.target.value })} />
        <div className="grid grid-cols-2 gap-2 mt-2">
          <input type="datetime-local" style={field} value={evt.starts_at} onChange={(e) => setEvt({ ...evt, starts_at: e.target.value })} />
          <input style={field} placeholder={t('ltr_evt_venue')} value={evt.venue} onChange={(e) => setEvt({ ...evt, venue: e.target.value })} />
        </div>
        <button type="button" disabled={busy} onClick={() => void saveEvent()} className="rounded-lg px-4 py-1.5 text-sm font-semibold mt-2" style={btn}>
          {t('ltr_evt_new')}
        </button>
        <p className="text-xs mt-2" style={{ color: 'var(--color-text-secondary)' }}>
          {t('ltr_evt_note')}
        </p>
      </section>

      {/* ── 模板 ─────────────────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-5" style={card}>
        <h3 className="font-bold mb-2">{t('ltr_tab_templates')}</h3>
        <ul className="text-sm mb-2">
          {templates.length === 0 ? (
            <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
          ) : (
            templates.map((x) => (
              <li key={x.id} className="py-1">
                {x.name} ｜ {t(`ltr_purpose_${x.purpose}`)} ｜ {x.locale}
              </li>
            ))
          )}
        </ul>
        <input style={field} placeholder={t('ltr_tpl_name')} value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} />
        <div className="grid grid-cols-2 gap-2 mt-2">
          <select style={field} value={tpl.purpose} onChange={(e) => setTpl({ ...tpl, purpose: e.target.value })}>
            {['invite', 'sponsor', 'thanks', 'other'].map((p) => (
              <option key={p} value={p}>
                {t(`ltr_purpose_${p}`)}
              </option>
            ))}
          </select>
          <select style={field} value={tpl.locale} onChange={(e) => setTpl({ ...tpl, locale: e.target.value })}>
            {['zh-HK', 'zh-CN', 'en'].map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <input style={field} placeholder={t('ltr_ai_brief')} value={tpl.brief} onChange={(e) => setTpl({ ...tpl, brief: e.target.value })} />
          <button type="button" disabled={busy} onClick={() => void aiDraft()} className="rounded-lg px-3 py-1.5 text-sm font-semibold" style={btn}>
            {t('ltr_ai_draft')}
          </button>
        </div>
        <input
          style={{ ...field, marginTop: 8 }}
          placeholder={t('ltr_tpl_subject')}
          value={tpl.subject}
          onChange={(e) => setTpl({ ...tpl, subject: e.target.value })}
        />
        <textarea
          style={{ ...field, marginTop: 8 }}
          rows={6}
          placeholder={t('ltr_tpl_body')}
          value={tpl.body}
          onChange={(e) => setTpl({ ...tpl, body: e.target.value })}
        />
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
          {t('ltr_tpl_fields', { fields: '{{name}} {{company}} {{title}} {{event_name}} {{event_date}} {{rsvp_link}}' })}
        </p>
        <button type="button" disabled={busy} onClick={() => void saveTemplate()} className="rounded-lg px-4 py-1.5 text-sm font-semibold mt-2" style={btn}>
          {t('ltr_tpl_new')}
        </button>
      </section>

      {/* ── 名單 ─────────────────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-5" style={card}>
        <h3 className="font-bold mb-2">{t('ltr_tab_groups')}</h3>
        <ul className="text-sm mb-2">
          {groups.length === 0 ? (
            <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
          ) : (
            groups.map((g) => (
              <li key={g.id} className="py-1">
                {g.name} ｜ {t('ltr_grp_count', { n: g.recipient_count })}
              </li>
            ))
          )}
        </ul>
        <input style={field} placeholder={t('ltr_grp_name')} value={grp.name} onChange={(e) => setGrp({ ...grp, name: e.target.value })} />
        <textarea
          style={{ ...field, marginTop: 8 }}
          rows={4}
          placeholder={t('ltr_grp_text')}
          value={grp.text}
          onChange={(e) => setGrp({ ...grp, text: e.target.value })}
        />
        <button type="button" disabled={busy} onClick={() => void saveGroup()} className="rounded-lg px-4 py-1.5 text-sm font-semibold mt-2" style={btn}>
          {t('ltr_grp_new')}
        </button>
      </section>

      {/* ── 寄送批次 ─────────────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-5" style={card}>
        <h3 className="font-bold mb-2">{t('ltr_tab_sends')}</h3>
        <ul className="text-sm mb-2">
          {sends.length === 0 ? (
            <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
          ) : (
            sends.map((s) => (
              <li key={s.id} className="py-1">
                <Link to={`/admin/letters/${s.id}`} style={{ color: 'var(--color-primary)' }}>
                  {s.subject}
                </Link>{' '}
                ｜ {t(`ltr_status_${s.status}`)} ｜ {s.created_at.slice(0, 10)}
              </li>
            ))
          )}
        </ul>
        <div className="grid grid-cols-2 gap-2">
          <select style={field} value={pick.templateId} onChange={(e) => setPick({ ...pick, templateId: e.target.value })}>
            <option value="">{t('ltr_send_pick_tpl')}</option>
            {templates.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
          <select style={field} value={pick.groupId} onChange={(e) => setPick({ ...pick, groupId: e.target.value })}>
            <option value="">{t('ltr_send_pick_grp')}</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}（{g.recipient_count}）
              </option>
            ))}
          </select>
        </div>
        <select style={{ ...field, marginTop: 8 }} value={pick.eventId} onChange={(e) => setPick({ ...pick, eventId: e.target.value })}>
          <option value="">{t('ltr_send_pick_evt')}</option>
          {events.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>
        <button type="button" disabled={busy} onClick={() => void createSend()} className="rounded-lg px-4 py-1.5 text-sm font-semibold mt-2" style={btn}>
          {t('ltr_send_new')}
        </button>
        <p className="text-xs mt-2" style={{ color: 'var(--color-text-secondary)' }}>
          {t('ltr_rsvp_note')}
        </p>
      </section>
    </div>
  );
}
